import { CREATE_TASK_CONTROL_FIELDS, createTaskRequest, normalizeModelParameterSchema, TASK_STATUSES, type JsonObject, type ModelHttpOperation } from "@nexra/contracts";

import { hasTranslation, translateKey } from "./i18n";

type Endpoint = Readonly<{ key: string; protocol: string; operation: string; sourceEndpointKey?: string | null }>;

export type EndpointHttpOperation = ModelHttpOperation;

/** Project a code-owned Nexra category contract into the flat Task HTTP body. */
export function taskEndpointParameterSchema(source: JsonObject, modelId: string): JsonObject {
  const schema = normalizeModelParameterSchema(source);
  const input = structuredClone(schema.input) as JsonObject;
  const reserved = new Set<string>([...CREATE_TASK_CONTROL_FIELDS, "input"]);
  const parameterProperties = input.properties && typeof input.properties === "object" && !Array.isArray(input.properties)
    ? Object.fromEntries(Object.entries(input.properties).filter(([key]) => !reserved.has(key)))
    : {};
  const required = Array.isArray(input.required)
    ? input.required.filter((key): key is string => typeof key === "string" && !reserved.has(key))
    : [];
  const examples = (Array.isArray(schema.examples) ? schema.examples : []).flatMap(parameters => {
    if (!parameters || typeof parameters !== "object" || Array.isArray(parameters)) return [];
    const categoryInput = Object.fromEntries(Object.entries(parameters).filter(([key]) => !reserved.has(key))) as JsonObject;
    return [createTaskRequest(modelId, categoryInput)];
  });
  return {
    headers: {
      type: "object",
      properties: {
        Authorization: { type: "string", title: "API Key", description: translateKey("taskDocs.authorization") },
        "Idempotency-Key": { type: "string", title: translateKey("taskDocs.idempotencyTitle"), description: translateKey("taskDocs.idempotency") },
        "Content-Type": { const: "application/json", title: translateKey("taskDocs.requestFormat") },
      },
      required: ["Authorization", "Idempotency-Key", "Content-Type"],
    },
    input: {
      ...input,
      type: "object",
      properties: {
        model: { type: "string", title: translateKey("taskDocs.publicModelId"), minLength: 1, description: translateKey("taskDocs.selectModel", { modelId }), examples: [modelId] },
        ...parameterProperties,
        callback_url: {
          type: "string", format: "uri", pattern: "^https://", maxLength: 2048, title: translateKey("taskDocs.callbackTitle"),
          description: translateKey("taskDocs.callback"),
          examples: ["https://example.com/callback?token=your-per-task-token"],
        },
        metadata: { type: "object", additionalProperties: true, title: translateKey("taskDocs.metadataTitle"), description: translateKey("taskDocs.metadata"), examples: [{ orderId: "order_123" }] },
      },
      required: ["model", ...required],
    },
    output: {
      type: "object", description: translateKey("taskDocs.taskResult"),
      properties: {
        id: { type: "string", title: translateKey("taskDocs.taskId") },
        requestId: { type: "string", title: translateKey("taskDocs.requestId"), description: translateKey("taskDocs.requestDetails") },
        status: { type: "string", enum: [...TASK_STATUSES], title: translateKey("taskDocs.taskStatus") },
        output: { ...schema.output as JsonObject, title: translateKey("taskDocs.modelOutput") },
        assets: { type: "array", title: translateKey("taskDocs.assetsTitle"), description: translateKey("taskDocs.assets"), items: { type: "object", additionalProperties: true, properties: {
          id: { type: "string", title: translateKey("taskDocs.assetId") }, kind: { type: "string", title: translateKey("taskDocs.assetKind") }, status: { type: "string", title: translateKey("taskDocs.assetStatus") },
          url: { type: "string", format: "uri", title: translateKey("taskDocs.assetUrl") }, downloadUrl: { type: "string", format: "uri", title: translateKey("taskDocs.downloadUrl") },
        } } },
        error: { type: "object", title: translateKey("taskDocs.error"), additionalProperties: true, properties: { code: { type: "string", title: translateKey("taskDocs.errorCode") }, message: { type: "string", title: translateKey("taskDocs.errorMessage") } }, required: ["code", "message"] },
        model: { type: "string", title: translateKey("taskDocs.publicModelId") },
      },
      required: ["id", "model", "status"],
    },
    examples,
  };
}

export type EndpointOperation = Readonly<{
  key: string;
  endpointKey?: string;
  title: string;
  method: string;
  path: string;
  documentationUrl?: string;
  schema: JsonObject;
  noRequestBody?: boolean;
  responseStatus?: number;
  requestExamples?: string[];
  taskEnvelope?: boolean;
  idempotencyKey?: boolean;
}>;

type ProjectionContext = Readonly<{ source: JsonObject; modelId: string; operation: EndpointHttpOperation }>;
type Projection = (context: ProjectionContext) => Pick<EndpointOperation, "noRequestBody" | "schema" | "taskEnvelope">;

function requestProjection({ source, operation }: ProjectionContext): ReturnType<Projection> {
  const schema = structuredClone(normalizeModelParameterSchema(source));
  if (operation.bodyParameters) {
    schema.input = structuredClone(operation.bodyParameters) as JsonObject;
    schema.examples = structuredClone(operation.bodyParameters.examples ?? []);
  }
  if (operation.requestBody) projectRequestBody(schema, operation.requestBody);
  if (operation.headerParameters) schema.headers = structuredClone(operation.headerParameters) as JsonObject;
  if (operation.pathParameters) schema.path = structuredClone(operation.pathParameters) as JsonObject;
  if (operation.queryParameters) schema.query = structuredClone(operation.queryParameters) as JsonObject;
  if (operation.responseBody) schema.output = structuredClone(operation.responseBody) as JsonObject;
  return { schema };
}

function projectRequestBody(
  schema: JsonObject,
  projection: NonNullable<EndpointHttpOperation["requestBody"]>,
): void {
  const input = schema.input;
  if (!input || typeof input !== "object" || Array.isArray(input)) throw new Error("HTTP request projection requires an object input schema");
  const properties = input.properties;
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) throw new Error("HTTP request projection requires input properties");
  const selected = Object.fromEntries(projection.properties.map((field) => {
    if (!(field in properties)) throw new Error(`HTTP request projection references an unknown field: ${field}`);
    return [field, structuredClone(properties[field]!)];
  }));
  const { allOf: _allOf, anyOf: _anyOf, oneOf: _oneOf, ...annotations } = input;
  schema.input = {
    ...annotations,
    properties: selected,
    required: [...projection.required],
    additionalProperties: false,
    ...(projection.oneOfRequired?.length
      ? { oneOf: projection.oneOfRequired.map((required) => ({ required: [...required] })) }
      : {}),
  };
  if (Array.isArray(schema.examples)) {
    const allowed = new Set(projection.properties);
    schema.examples = schema.examples.filter((example) => {
      if (!example || typeof example !== "object" || Array.isArray(example)) return false;
      if (Object.keys(example).some((field) => !allowed.has(field))) return false;
      if (projection.required.some((field) => !(field in example))) return false;
      if (!projection.oneOfRequired?.length) return true;
      return projection.oneOfRequired.filter((group) => group.every((field) => field in example)).length === 1;
    });
  }
}

function bearerRequestProjection(context: ProjectionContext): ReturnType<Projection> {
  const { schema } = requestProjection(context);
  if (!schema.headers) schema.headers = requestHeaders(true, context.operation.idempotencyKey);
  return { schema };
}

function nativeTaskCreateProjection(context: ProjectionContext): ReturnType<Projection> {
  const { schema } = requestProjection(context);
  const properties = (schema.input as JsonObject | undefined)?.properties as JsonObject | undefined;
  if (properties?.callback_url) {
    const callback = properties.callback_url as JsonObject;
    const descriptionKey = (callback["x-ui"] as JsonObject | undefined)?.descriptionKey;
    properties.callback_url = { ...callback, title: translateKey("taskDocs.nativeCallbackTitle"),
      description: translateKey(typeof descriptionKey === "string" ? descriptionKey : "taskDocs.nativeCallback") };
  }
  const { result: _result, ...create } = schema;
  if (!create.headers) create.headers = requestHeaders(true);
  return { schema: create };
}

function nativeTaskQueryProjection({ source, operation }: ProjectionContext): ReturnType<Projection> {
  const schema = normalizeModelParameterSchema(source);
  const location: JsonObject = operation.bodyParameters
    ? { input: structuredClone(operation.bodyParameters),
      ...(operation.pathParameters ? { path: structuredClone(operation.pathParameters) } : {}),
      ...(operation.queryParameters ? { query: structuredClone(operation.queryParameters) } : {}),
      ...(Array.isArray(operation.bodyParameters.examples) ? { examples: structuredClone(operation.bodyParameters.examples) } : {}) }
    : operation.queryParameters
    ? { query: structuredClone(operation.queryParameters), ...(operation.pathParameters ? { path: structuredClone(operation.pathParameters) } : {}) }
    : { path: operation.pathParameters ? structuredClone(operation.pathParameters) : taskIdPath(false) };
  const output = operation.responseBody ?? schema.result;
  return { noRequestBody: !operation.bodyParameters, schema: { headers: operation.headerParameters ? structuredClone(operation.headerParameters) : requestHeaders(Boolean(operation.bodyParameters)), ...location, ...(output ? { output: structuredClone(output) } : {}) } };
}

function nativeTaskDeleteProjection(context: ProjectionContext): ReturnType<Projection> {
  if (context.operation.bodyParameters) return nativeTaskQueryProjection(context);
  return { noRequestBody: true, schema: {
    headers: requestHeaders(false),
    path: { ...taskIdPath(false), description: translateKey("taskDocs.deleted") },
    output: { type: "object", properties: {}, additionalProperties: false, examples: [{}] },
  } };
}

function managedTaskCreateProjection({ source, modelId }: ProjectionContext): ReturnType<Projection> {
  const { output: _output, ...create } = taskEndpointParameterSchema(source, modelId);
  return { taskEnvelope: true, schema: { ...create, output: {
    type: "object", additionalProperties: false, description: translateKey("taskDocs.accepted"),
    properties: { id: { type: "string", title: translateKey("taskDocs.taskId") }, status: { type: "string", enum: [...TASK_STATUSES], title: translateKey("taskDocs.taskStatus") } },
    required: ["id", "status"], examples: [{ id: "task_123", status: "queued" }],
  } } };
}

function managedTaskQueryProjection({ source, modelId }: ProjectionContext): ReturnType<Projection> {
  const schema = taskEndpointParameterSchema(source, modelId);
  return { noRequestBody: true, taskEnvelope: true, schema: {
    headers: requestHeaders(false), path: taskIdPath(true), output: { ...schema.output as JsonObject, description: translateKey("taskDocs.queried") },
  } };
}

function managedTaskCancelProjection(): ReturnType<Projection> {
  return { noRequestBody: true, taskEnvelope: true, schema: {
    headers: requestHeaders(false), path: taskIdPath(true), output: {
      type: "object", additionalProperties: false, description: translateKey("taskDocs.cancelled"),
      properties: { id: { type: "string", title: translateKey("taskDocs.taskId") }, status: { type: "string", enum: [...TASK_STATUSES], title: translateKey("taskDocs.taskStatus") } },
      required: ["id", "status"], examples: [{ id: "task_123", status: "cancelled" }],
    },
  } };
}

const schemaProjections: Readonly<Record<EndpointHttpOperation["schemaProjection"], Projection>> = {
  request: requestProjection,
  "bearer-request": bearerRequestProjection,
  "managed-task-create": managedTaskCreateProjection,
  "managed-task-query": managedTaskQueryProjection,
  "managed-task-cancel": managedTaskCancelProjection,
  "native-task-create": nativeTaskCreateProjection,
  "native-task-query": nativeTaskQueryProjection,
  "native-task-delete": nativeTaskDeleteProjection,
};

function requestHeaders(contentType: boolean, idempotencyKey = false): JsonObject {
  const authorization = { type: "string", title: "API Key", description: translateKey("taskDocs.authorization") };
  const properties: JsonObject = { Authorization: authorization };
  if (idempotencyKey) properties["Idempotency-Key"] = { type: "string", title: translateKey("taskDocs.idempotencyTitle"), description: translateKey("taskDocs.idempotency") };
  if (contentType) properties["Content-Type"] = { const: "application/json", title: translateKey("taskDocs.requestFormat") };
  return { type: "object", properties, required: Object.keys(properties) };
}

function taskIdPath(managed: boolean): JsonObject {
  return { type: "object", properties: { id: { type: "string", title: managed ? translateKey("taskDocs.taskId") : translateKey("taskDocs.upstreamTaskId"), description: translateKey("taskDocs.returnedId") } }, required: ["id"] };
}

/** Render the HTTP operations resolved by the code-owned protocol definition. */
export function endpointOperations(endpoint: Readonly<{ key?: string; method: string; path: string; operation?: string; protocol?: string; documentationUrl?: string | null; httpOperations?: readonly EndpointHttpOperation[] }>, source: JsonObject, modelId: string): EndpointOperation[] {
  const specifications = endpoint.httpOperations?.length ? endpoint.httpOperations : [{
    key: "request", kind: "request", httpMethod: endpoint.method as EndpointHttpOperation["httpMethod"],
    pathTemplate: endpoint.path, schemaProjection: "request",
  } satisfies EndpointHttpOperation];
  return specifications.map(specification => {
    const projection = schemaProjections[specification.schemaProjection]({ source, modelId, operation: specification });
    const requestTitleKey = `parameters.requestOperation.${specification.key}`;
    const resourceTitleKey = `operations.${endpoint.operation?.replaceAll(".", "-")}`;
    const operation: EndpointOperation = {
      key: specification.key,
      endpointKey: endpoint.key,
      title: specification.kind === "create" && endpoint.operation?.startsWith("voice.") && hasTranslation(resourceTitleKey) ? translateKey(resourceTitleKey)
        : hasTranslation(requestTitleKey) ? translateKey(requestTitleKey)
        : translateKey(`parameters.operation.${endpoint.protocol === "xai-video-task" && ["edit", "extend"].includes(specification.key)
        ? `video${specification.key === "edit" ? "Edit" : "Extend"}`
        : specification.kind === "generate" && endpoint.operation === "language.generate" ? "request"
        : specification.kind === "generate" && endpoint.operation === "audio.generate" ? "audioGenerate" : specification.kind}`),
      method: specification.httpMethod,
      path: specification.pathTemplate,
      documentationUrl: specification.documentationUrl ?? endpoint.documentationUrl ?? undefined,
      responseStatus: specification.responseStatus,
      idempotencyKey: specification.idempotencyKey,
      ...projection,
    };
    return { ...operation, requestExamples: operation.noRequestBody
      ? [operationCurlExample(operation)]
      : (Array.isArray(operation.schema.examples) ? operation.schema.examples : []).map(example => operationCurlExample(operation, example)) };
  });
}


function operationCurlExample(operation: EndpointOperation, body?: unknown): string {
  const variables = new Map<string, string>();
  const pathProperties = schemaProperties(operation.schema.path);
  let path = operation.path.replace(/\{([^}]+)\}/g, (_match, name: string) => {
    const example = schemaExample(pathProperties[name]);
    if (typeof example === "string") return encodeURIComponent(example);
    const variable = name === "id" ? "TASK_ID" : name.replace(/[^a-z0-9_]/gi, "_").toUpperCase();
    variables.set(variable, `your_${variable.toLowerCase()}`);
    return `$${variable}`;
  });
  const existingQuery = new URLSearchParams(path.split("?")[1]);
  for (const [name, field] of Object.entries(schemaProperties(operation.schema.query))) {
    const example = schemaExample(field);
    if (example === undefined || existingQuery.has(name)) continue;
    path += `${path.includes("?") ? "&" : "?"}${encodeURIComponent(name)}=${encodeURIComponent(String(example))}`;
  }
  const headers = schemaProperties(operation.schema.headers);
  const contentType = schemaExample(headers["Content-Type"]);
  const multipart = contentType === "multipart/form-data";
  const lines = [`curl --request ${operation.method} "https://api.nexra-ai.com${path}"`];
  if (!operation.schema.headers) lines.push('  --header "Authorization: Bearer $NEXRA_API_KEY"');
  for (const [name, field] of Object.entries(headers)) {
    if (name.toLowerCase() === "content-type" || name.toLowerCase() === "idempotency-key") continue;
    const example = schemaExample(field);
    if (name.toLowerCase() === "authorization" && example === undefined) {
      lines.push('  --header "Authorization: Bearer $NEXRA_API_KEY"');
    } else if (["api-key", "x-api-key", "xi-api-key"].includes(name.toLowerCase()) && example === undefined) {
      lines.push(`  --header "${name}: $NEXRA_API_KEY"`);
    } else if (typeof example === "string" && /^\$[A-Z_][A-Z_0-9]*$/.test(example)) {
      lines.push(`  --header "${name}: ${example}"`);
    } else if (example !== undefined) lines.push(`  --header ${shellQuote(`${name}: ${String(example)}`)}`);
  }
  if (operation.idempotencyKey) lines.push('  --header "Idempotency-Key: $IDEMPOTENCY_KEY"');
  if (multipart && body && typeof body === "object" && !Array.isArray(body)) {
    const properties = schemaProperties(operation.schema.input);
    for (const [name, value] of Object.entries(body)) {
      const binary = hasBinaryField(properties[name]);
      const values = Array.isArray(value) ? value : [value];
      const field = binary ? multipartFieldName(name, properties[name]) : name;
      for (const item of values) {
        const text = typeof item === "object" ? JSON.stringify(item) : String(item);
        lines.push(`  ${binary ? "--form" : "--form-string"} ${shellQuote(`${field}=${binary ? "@" : ""}${text}`)}`);
      }
    }
  } else if (body !== undefined) {
    lines.push(`  --header ${shellQuote(`Content-Type: ${contentType ?? "application/json"}`)}`,
      `  --data ${shellQuote(JSON.stringify(body, null, 2))}`);
  }
  const setup = [...variables].map(([name, value]) => `${name}="${value}"\n`).join("");
  return `${setup}${lines.join(" \\\n")}`;
}

function schemaProperties(value: unknown): JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const properties = (value as JsonObject).properties;
  return properties && typeof properties === "object" && !Array.isArray(properties) ? properties : {};
}

function schemaExample(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const field = value as JsonObject;
  return field.const ?? (Array.isArray(field.examples) ? field.examples[0] : undefined);
}

function hasBinaryField(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const field = value as JsonObject;
  return field.contentMediaType === "application/octet-stream" || hasBinaryField(field.items)
    || [field.anyOf, field.oneOf].some(options => Array.isArray(options) && options.some(hasBinaryField));
}

function multipartFieldName(name: string, value: unknown): string {
  const field = value && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : {};
  return typeof field["x-multipart-name"] === "string" ? field["x-multipart-name"] : name;
}

/** File controls and multipart serialization read the same native operation fields. */
export function nativeFileFields(input: JsonObject) {
  const required = Array.isArray(input.required) ? input.required : [];
  return Object.entries(schemaProperties(input)).flatMap(([name, value]) => {
    if (!hasBinaryField(value)) return [];
    const field = value as JsonObject;
    const variants = [field, ...(Array.isArray(field.anyOf) ? field.anyOf : []), ...(Array.isArray(field.oneOf) ? field.oneOf : [])];
    const array = variants.find(variant => variant && typeof variant === "object" && !Array.isArray(variant) && variant.type === "array") as JsonObject | undefined;
    return [{ name, schema: field, formName: multipartFieldName(name, field), required: required.includes(name),
      multiple: Boolean(array), maxFiles: array && typeof array.maxItems === "number" ? array.maxItems : array ? undefined : 1 }];
  });
}

function shellQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

/** Parameter reuse does not merge wire protocols. Task is a parallel endpoint. */
export function modelApiEntries<T extends Endpoint>(endpoints: readonly T[]): T[] {
  return [...endpoints];
}

export function modelApiGroups<T extends Endpoint>(endpoints: readonly T[]): { protocol: string; endpoints: T[] }[] {
  const groups = new Map<string, { protocol: string; endpoints: T[] }>();
  for (const endpoint of endpoints) {
    const group = groups.get(endpoint.protocol);
    if (group) group.endpoints.push(endpoint);
    else groups.set(endpoint.protocol, { protocol: endpoint.protocol, endpoints: [endpoint] });
  }
  return [...groups.values()];
}

export function managedTaskEndpoint<T extends Endpoint>(endpoints: readonly T[], source?: T): T | undefined {
  return source ? endpoints.find((endpoint) => endpoint.sourceEndpointKey === source.key
    && endpoint.operation === source.operation) : undefined;
}

export function endpointEntryLabel(endpoint: Endpoint, entries: readonly Endpoint[]): string {
  const label = endpointProtocolLabel(endpoint.protocol);
  return entries.some(other => other.key !== endpoint.key && other.protocol === endpoint.protocol)
    ? `${label} · ${endpoint.operation}` : label;
}

export function endpointProtocolLabel(protocol: string): string {
  const labels: Record<string, string> = {
    "openai-chat-completions": "Chat Completions",
    "openai-responses": "Responses",
    "anthropic-messages": "Messages",
    "gemini-generate-content": "Gemini GenerateContent",
    "volcengine-tts": "Volcengine TTS API",
    "volcengine-cv": "Volcengine CV API",
    "volcengine-vod": "Volcengine VOD API",
    "google-vertex-image": "Google Vertex Image API",
    "ark-video-task": "ByteDance Video API",
    "ark-image": "ByteDance Image API",
    "agnes-image": "Agnes Image API",
    "agnes-video": "Agnes Video API",
    "ctyun-minimax-video": "CTYun MiniMax Video API",
    "ctyun-contents-video": "CTYun Video API",
    "elevenlabs-native-v1": "ElevenLabs Audio API",
    "fish-audio-tts": "Fish Audio TTS API",
    "dashscope-multimodal-generation": "Alibaba Cloud Multimodal Generation API",
    "dashscope-video-task": "Alibaba Cloud Video API",
    "minimax-video-v2": "MiniMax Video API",
    "minimax-audio": "MiniMax Audio API",
    "xai-image": "xAI Image API",
    "azure-openai-image": "Azure OpenAI Image API",
    "runninghub-image": "RunningHub Image API",
    "xai-video-task": "xAI Video API",
    "nexra-task": "Nexra Task API",
    "kling-v4": "Kling 4.0 Native API",
    "nexra-voice": "Nexra Voice API",
  };
  return labels[protocol] ?? protocol;
}

/** A code endpoint alone is not an available supply. Match runtime eligibility. */
export function endpointChannelCount(endpointId: string | readonly string[], supplies: readonly {
  channelId: string; channelStatus: string; status: string; selected: boolean;
  routes: readonly { endpointId: string; status: string; weight: number }[];
}[]): number {
  return new Set(supplies.filter((supply) => supply.status === "active"
    && supply.channelStatus === "active" && supply.selected
    && supply.routes.some((route) => (typeof endpointId === "string" ? route.endpointId === endpointId : endpointId.includes(route.endpointId))
      && route.status === "active" && route.weight > 0))
    .map((supply) => supply.channelId)).size;
}
